import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as singleSpa from 'single-spa';
import isEmpty from 'lodash/isEmpty';
import i18next from 'i18next';

import { hasPermission, IPermissionsProps } from './PermissionsHelper';
import { APP_ROUTES_WITH_PERMISSIONS, Route } from '../config/routes';
import { addNotification } from '../main/store/EventData/actions/eventData';
import { EVENTS_WARNING } from '../main/constants/events';
import { permissionsEvaluationDecision } from './withPermissionsHOCFlow';
import { selectUserInfo } from '../main/store/User/reducer';
import { User } from '../models/User';
import { selectActiveProducts, selectExtendedProducts } from '../main/store/Products/reducer';
import { getHomeScreenUrl } from './homeScreenUrlHelper';
import { BEFORE_ROUTING_EVENT } from '../constants/SingleSpaEvents';

export class CheckPermissionResponse {
    route: Route;
    isAllowedUrl: boolean;
    hasAction: boolean;
}

const goToFallbackRouteHandler = (route: string) => {
    singleSpa.navigateToUrl(route);
};

const checkPermission = (hash: string, permissionsProps: IPermissionsProps) => {
    let response = new CheckPermissionResponse();
    response.isAllowedUrl = true;

    for (let route of APP_ROUTES_WITH_PERMISSIONS) {
        const { permissionsShouldBeEvaluated, hasAction } = permissionsEvaluationDecision(hash, route);

        if (hasAction) {
            response.isAllowedUrl = false;
            response.route = route;
            response.hasAction = true;
            break;
        } else if (permissionsShouldBeEvaluated) {
            response.isAllowedUrl = route.PERMISSIONS.every(permission => hasPermission(permissionsProps, permission));
            response.route = route;
            break;
        }
    }

    return response;
};

export const withPermissions = (Component: any) => (props: any) => {
    const user: User | undefined = useSelector(selectUserInfo);
    const extendedProducts = useSelector(selectExtendedProducts);
    const activeProducts = useSelector(selectActiveProducts);
    const permissions = props.permissions;

    const dispatch = useDispatch();

    const onNavigate = (permissionsProps: IPermissionsProps) => {
        const hash = window.location.hash;
        const permissionResponse: CheckPermissionResponse = checkPermission(hash, permissionsProps);
        const { isAllowedUrl, route } = permissionResponse;

        const hasRouteFallbackProp = !!route?.routeFallback;
        const isRestrictedWithNoCallback = !isAllowedUrl && !hasRouteFallbackProp;
        const isRestrictedWithCallback = !isAllowedUrl && hasRouteFallbackProp;

        const homeScreen = getHomeScreenUrl(permissionsProps.user?.homeScreen);

        if (permissionResponse.hasAction) {
            const payload = permissionResponse.route.canActivate(permissionsProps);

            if (payload) {
                goToFallbackRouteHandler(homeScreen);
            }

            dispatch({
                type: permissionResponse.route.actionType,
                payload: payload,
            });
        } else if (isRestrictedWithNoCallback) {
            dispatch(
                addNotification({
                    detail: {
                        content: i18next.t('default.dashboard._navbar.you_dont_have_permissions'),
                    },
                    type: EVENTS_WARNING,
                })
            );
            goToFallbackRouteHandler(homeScreen);
        } else if (isRestrictedWithCallback) {
            const fallbackRoute = hash.replace(route.BASE, route.routeFallback ?? '');
            checkFallbackRoute(fallbackRoute, permissionsProps)
                ? goToFallbackRouteHandler(fallbackRoute ?? homeScreen)
                : goToFallbackRouteHandler(homeScreen);
        }
    };

    const checkFallbackRoute = (fallbackRoute: string | undefined, permissionsProps: IPermissionsProps) => {
        if (fallbackRoute !== undefined) {
            if (checkPermission(fallbackRoute, permissionsProps).isAllowedUrl) {
                return true;
            }
        }
        return false;
    };

    useEffect(() => {
        const hasUserSelector = !isEmpty(user) && !isEmpty(permissions);
        const permissionsProps: IPermissionsProps = {
            user,
            extendedProducts,
            activeProducts,
            permissions,
        };

        const handlePermissions = () => onNavigate(permissionsProps);

        if (hasUserSelector && extendedProducts) {
            const hasPermissionsDefined = permissions && permissions.length > 0;
            window.addEventListener(BEFORE_ROUTING_EVENT, handlePermissions, false);

            if (hasPermissionsDefined) onNavigate(permissionsProps);
        }

        return () => window.removeEventListener(BEFORE_ROUTING_EVENT, handlePermissions, false);
    }, [user, extendedProducts, activeProducts, permissions]);

    return <Component {...props} />;
};
